import { supabase } from "./supabase";
import type { AIInsight, ClusterHistory, CoFacilitatorRole, Event, EventFacilitator, EventRole, EventStats, EventTool, PresentationKey, Question, QuestionConfig, QuestionType, Response, SegmentRow, SeedCluster, Stream, ToolAccent } from "./types";
import { TOOL_DEFAULTS, type ToolDefault } from "./toolDefaults";
import { slugifyGeoTag } from "./stadtteilSlugs";
import { isVisibleResponse, respondentNumbers } from "./responses";
import { answerLabel, exportDistribution, type RespondentBase } from "./stageViewModels";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// ─── Plans & quotas ──────────────────────────────────────────────────────

export type PlanTier = "free" | "pro" | "unlimited";

export interface PlanLimits {
  max_active_events: number | null;
  max_participants_per_event: number | null;
  ai_budget_usd: number;
}

export interface MyPlan {
  plan: PlanTier;
  limits: PlanLimits;
}

const PLAN_LIMITS: Record<PlanTier, PlanLimits> = {
  free: { max_active_events: 1, max_participants_per_event: 40, ai_budget_usd: 2 },
  pro: { max_active_events: null, max_participants_per_event: 250, ai_budget_usd: 15 },
  unlimited: { max_active_events: null, max_participants_per_event: null, ai_budget_usd: 100 },
};

// Reads the caller's plan row; falls back to 'free' if none exists yet.
export async function getMyPlan(): Promise<MyPlan> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { plan: "free", limits: PLAN_LIMITS.free };
  const { data } = await supabase
    .from("user_plans")
    .select("plan")
    .eq("user_id", user.id)
    .maybeSingle();
  const plan = (((data as { plan?: PlanTier } | null)?.plan) ?? "free");
  return { plan, limits: PLAN_LIMITS[plan] ?? PLAN_LIMITS.free };
}

// ─── Events ────────────────────────────────────────────────────────────────

export async function getMyEvents(): Promise<Array<Event & { role: EventRole }>> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  const { data: events } = await supabase
    .from("events")
    .select("*")
    .order("created_at", { ascending: false });
  if (!events) return [];

  // Resolve role per event in JS. RLS has already restricted what came back;
  // we just need to label each row.
  const userEmailLower = (user.email ?? "").toLowerCase();
  const orFilter = userEmailLower
    ? `user_id.eq.${user.id},email.ilike.${userEmailLower}`
    : `user_id.eq.${user.id}`;
  const { data: facRows } = await supabase
    .from("event_facilitators")
    .select("event_id, role, user_id, email")
    .or(orFilter);

  const roleByEvent = new Map<string, EventRole>();
  for (const r of facRows ?? []) {
    roleByEvent.set(r.event_id, r.role as EventRole);
  }

  return events.map((e) => ({
    ...e,
    role: (e.owner_id === user.id ? "owner" : roleByEvent.get(e.id) ?? "co_mod") as EventRole,
  }));
}

export async function getEventByJoinCode(joinCode: string): Promise<Event | null> {
  const { data } = await supabase
    .from("events")
    .select("*")
    .eq("join_code", joinCode.toUpperCase())
    .maybeSingle();
  return data;
}

export async function getEventById(eventId: string): Promise<Event | null> {
  const { data } = await supabase
    .from("events")
    .select("*")
    .eq("id", eventId)
    .maybeSingle();
  return data;
}

function generateJoinCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  return Array.from({ length: 5 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
}

export async function createEvent(
  title: string,
  description: string,
  eventMode: "facilitated" | "survey" = "facilitated",
  rotationIntervalSeconds: number | null = null,
): Promise<Event> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("Not authenticated");

  let joinCode = generateJoinCode();
  let attempts = 0;
  while (attempts < 5) {
    const existing = await getEventByJoinCode(joinCode);
    if (!existing) break;
    joinCode = generateJoinCode();
    attempts++;
  }

  const { limits } = await getMyPlan();

  const { data, error } = await supabase
    .from("events")
    .insert({
      owner_id: user.id,
      title,
      description,
      join_code: joinCode,
      event_mode: eventMode,
      rotation_interval_seconds: rotationIntervalSeconds,
      moderation_enabled: eventMode === "survey",
      ai_budget_usd: limits.ai_budget_usd,
    })
    .select()
    .single();
  if (error) {
    // Surfaces the enforce_event_quota trigger's RAISE EXCEPTION as a readable
    // German message instead of a raw Postgres error.
    if (error.message?.includes("plan_event_limit")) {
      throw new Error(
        "Dein aktueller Plan erlaubt nur ein aktives Event gleichzeitig. Schließe ein laufendes Event ab oder wechsle den Plan, um weitere Events zu starten.",
      );
    }
    throw error;
  }
  return data;
}

export async function advanceSurveyQuestion(eventId: string, currentIndex: number, totalQuestions: number) {
  if (totalQuestions === 0) return;
  const next = (currentIndex + 1) % totalQuestions;
  const { error } = await supabase
    .from("events")
    .update({ displayed_question_index: next })
    .eq("id", eventId);
  if (error) throw error;
}

export async function updateEvent(id: string, updates: Partial<Event>) {
  const { error } = await supabase.from("events").update(updates).eq("id", id);
  if (error) throw error;
}

export async function deleteEvent(id: string) {
  const { error } = await supabase.from("events").delete().eq("id", id);
  if (error) throw error;
}

// Deletes the signed-in user's account and all their owned events via the
// delete-account edge function (service role), then signs out locally.
export async function deleteMyAccount(): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not authenticated");

  const res = await fetch(`${SUPABASE_URL}/functions/v1/delete-account`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({}),
  });

  const raw = await res.text();
  let data: Record<string, unknown> = {};
  try { data = raw ? JSON.parse(raw) : {}; } catch { data = { error: raw }; }
  if (!res.ok) {
    throw new Error(typeof data.error === "string" ? data.error : `Kontolöschung fehlgeschlagen (${res.status})`);
  }
  await supabase.auth.signOut();
}

// ─── Co-Facilitators ───────────────────────────────────────────────────────

export async function listEventFacilitators(eventId: string): Promise<EventFacilitator[]> {
  const { data, error } = await supabase
    .from("event_facilitators")
    .select("*")
    .eq("event_id", eventId)
    .order("invited_at", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// ─── Presentation keys (REASONZ Live) ──────────────────────────────────────

export async function listPresentationKeys(eventId: string): Promise<PresentationKey[]> {
  const { data, error } = await supabase
    .from("presentation_keys")
    .select("*")
    .eq("event_id", eventId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

/** Returns the plaintext key. It is not stored anywhere and cannot be shown again. */
export async function createPresentationKey(eventId: string, label: string, validDays: number): Promise<string> {
  const { data, error } = await supabase.rpc("create_presentation_key", {
    p_event: eventId,
    p_label: label,
    p_valid_days: validDays,
  });
  if (error) throw error;
  return data as string;
}

export async function revokePresentationKey(keyId: string): Promise<void> {
  const { error } = await supabase.rpc("revoke_presentation_key", { p_key_id: keyId });
  if (error) throw error;
}

export async function inviteEventFacilitator(
  eventId: string,
  email: string,
  role: CoFacilitatorRole,
): Promise<{ status: "added" | "invited"; facilitator_id: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not authenticated");

  const res = await fetch(`${SUPABASE_URL}/functions/v1/invite-cofacilitator`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ event_id: eventId, email, role }),
  });

  const raw = await res.text();
  let data: Record<string, unknown> = {};
  try { data = raw ? JSON.parse(raw) : {}; } catch { data = { error: raw }; }
  if (!res.ok) {
    throw new Error(typeof data.error === "string" ? data.error : `Invite failed (${res.status})`);
  }
  return data as { status: "added" | "invited"; facilitator_id: string };
}

export async function updateEventFacilitatorRole(
  facilitatorId: string,
  role: CoFacilitatorRole,
): Promise<void> {
  const { error } = await supabase
    .from("event_facilitators")
    .update({ role })
    .eq("id", facilitatorId);
  if (error) throw error;
}

export async function removeEventFacilitator(facilitatorId: string): Promise<void> {
  const { error } = await supabase
    .from("event_facilitators")
    .delete()
    .eq("id", facilitatorId);
  if (error) throw error;
}

export async function claimMyPendingInvites(): Promise<void> {
  const { error } = await supabase.rpc("claim_my_pending_invites");
  if (error) throw error;
}

// ─── Sessions (legacy single-session support) ──────────────────────────────

export async function getSession() {
  const { data } = await supabase.from("sessions").select("*").maybeSingle();
  return data;
}

// ─── Questions ─────────────────────────────────────────────────────────────

export async function getQuestions(sessionId: string) {
  const { data } = await supabase
    .from("questions")
    .select("*")
    .eq("session_id", sessionId)
    .order("order_index", { ascending: true });
  return data ?? [];
}

export async function getQuestionsByEvent(eventId: string) {
  const { data } = await supabase
    .from("questions")
    .select("*")
    .eq("event_id", eventId)
    .order("order_index", { ascending: true });
  return data ?? [];
}

export async function createQuestion(eventId: string, params: {
  text: string;
  hint?: string;
  priming_text?: string;
  order_index: number;
  question_type?: QuestionType;
  config?: QuestionConfig;
}) {
  const { data, error } = await supabase
    .from("questions")
    .insert({
      event_id: eventId,
      text: params.text,
      hint: params.hint ?? "",
      priming_text: params.priming_text ?? "",
      order_index: params.order_index,
      question_type: params.question_type ?? "open_text",
      config: params.config ?? {},
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateQuestionConfig(id: string, config: QuestionConfig) {
  const { error } = await supabase.from("questions").update({ config }).eq("id", id);
  if (error) throw error;
}

export async function deleteQuestion(id: string) {
  const { error } = await supabase.from("questions").delete().eq("id", id);
  if (error) throw error;
}

// ─── Device salt ───────────────────────────────────────────────────────────

const DEVICE_SALT_KEY = "reasonz_device_salt";

/**
 * Reads a stable per-device UUID from localStorage, creating one if absent.
 * Used as entropy in the server-side fingerprint hash.
 */
export function getDeviceSalt(): string {
  try {
    const existing = localStorage.getItem(DEVICE_SALT_KEY);
    if (existing) return existing;
    const salt = crypto.randomUUID();
    localStorage.setItem(DEVICE_SALT_KEY, salt);
    return salt;
  } catch {
    // localStorage may be unavailable (private browsing); fall back to a
    // per-session value that won't persist but still provides entropy.
    return crypto.randomUUID();
  }
}

// ─── Typed submission errors ────────────────────────────────────────────────

export class SubmissionError extends Error {
  code: "rate_limit" | "paused" | "closed" | "unknown";
  constructor(message: string, code: "rate_limit" | "paused" | "closed" | "unknown") {
    super(message);
    this.name = "SubmissionError";
    this.code = code;
  }
}

// ─── Responses ─────────────────────────────────────────────────────────────

/**
 * When `limit` is set, returns the `limit` most recently submitted responses
 * (re-sorted back to ascending so callers can treat the array as a chronological
 * feed). Without `limit`, returns the full history.
 */
export async function getResponses(
  questionId: string,
  opts: { limit?: number } = {},
) {
  const { limit } = opts;

  if (limit !== undefined) {
    const { data } = await supabase
      .from("responses")
      .select("*")
      .eq("question_id", questionId)
      .order("submitted_at", { ascending: false })
      .limit(limit);
    return (data ?? []).slice().reverse();
  }

  const { data } = await supabase
    .from("responses")
    .select("*")
    .eq("question_id", questionId)
    .order("submitted_at", { ascending: true });
  return data ?? [];
}

export type SubmitResponseInput = {
  questionId: string;
  text?: string;
  streamId?: string | null;
  rating?: number;
  choiceKey?: string;
  /** Multi-select multiple_choice: the full set of selected keys (one row per key). */
  choiceKeys?: string[];
  isOther?: boolean;
};

export async function submitResponse(input: SubmitResponseInput): Promise<Response> {
  const deviceSalt = getDeviceSalt();
  const body: Record<string, unknown> = {
    question_id: input.questionId,
    text: (input.text ?? "").trim(),
    device_salt: deviceSalt,
  };
  if (input.streamId != null) body.stream_id = input.streamId;
  if (input.rating != null) body.rating = input.rating;
  if (input.choiceKey != null) body.choice_key = input.choiceKey;
  if (input.choiceKeys != null) body.choice_keys = input.choiceKeys;
  if (input.isOther != null) body.is_other = input.isOther;

  const res = await fetch(`${SUPABASE_URL}/functions/v1/submit-response`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify(body),
  });

  const raw = await res.text();
  let data: Record<string, unknown> = {};
  try { data = raw ? JSON.parse(raw) : {}; } catch { data = { error: raw }; }

  if (res.status === 429) {
    throw new SubmissionError(
      typeof data.error === "string" ? data.error : "Du hast schon viel beigetragen — danke!",
      "rate_limit",
    );
  }
  if (res.status === 423) {
    const closed = data.code === "closed";
    throw new SubmissionError(
      typeof data.error === "string"
        ? data.error
        : closed
          ? "Diese Frage nimmt gerade keine Antworten entgegen."
          : "Annahme von Beiträgen ist derzeit pausiert.",
      closed ? "closed" : "paused",
    );
  }
  if (!res.ok) {
    throw new SubmissionError(
      typeof data.error === "string" ? data.error : `Senden fehlgeschlagen (${res.status})`,
      "unknown",
    );
  }
  const row = data as unknown as Response;
  if (row?.id && row.moderation_status === "pending") {
    void triggerModeration(row.id);
  }
  return row;
}

// ─── Clusters ──────────────────────────────────────────────────────────────

export async function getLatestClusters(questionId: string) {
  const { data: run } = await supabase
    .from("cluster_runs")
    .select("id")
    .eq("question_id", questionId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (!run) return [];
  const { data } = await supabase
    .from("clusters")
    .select("*")
    .eq("run_id", run.id)
    .order("rank_order", { ascending: true });
  return data ?? [];
}

export async function getThemeHistory(questionId: string): Promise<ClusterHistory[]> {
  const { data } = await supabase
    .from("cluster_history")
    .select("*")
    .eq("question_id", questionId)
    .order("snapshot_at", { ascending: true });
  return (data ?? []) as ClusterHistory[];
}

// ─── Streams ───────────────────────────────────────────────────────────────

const STREAM_CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";

function generateStreamCode(length = 5): string {
  return Array.from(
    { length },
    () => STREAM_CODE_CHARS[Math.floor(Math.random() * STREAM_CODE_CHARS.length)],
  ).join("");
}

export async function listStreams(eventId: string): Promise<Stream[]> {
  const { data } = await supabase
    .from("streams")
    .select("*")
    .eq("event_id", eventId)
    .order("sort_order", { ascending: true });
  return (data ?? []) as Stream[];
}

export async function createStream(
  eventId: string,
  params: { label: string; geo_tag?: string | null },
): Promise<Stream> {
  let lastError: Error | null = null;
  for (let attempt = 0; attempt < 5; attempt++) {
    const code = generateStreamCode();
    const { data, error } = await supabase
      .from("streams")
      .insert({ event_id: eventId, code, label: params.label, geo_tag: params.geo_tag ?? null })
      .select()
      .single();
    if (!error) return data as Stream;
    // 23505 = unique_violation; retry on collision
    if ((error as { code?: string }).code === "23505") {
      lastError = new Error(error.message);
      continue;
    }
    throw error;
  }
  throw lastError ?? new Error("Failed to create stream after 5 attempts");
}

export async function updateStream(
  id: string,
  updates: Partial<Pick<Stream, "label" | "geo_tag" | "sort_order">>,
): Promise<void> {
  const { error } = await supabase.from("streams").update(updates).eq("id", id);
  if (error) throw error;
}

export async function deleteStream(id: string): Promise<void> {
  const { error } = await supabase.from("streams").delete().eq("id", id);
  if (error) throw error;
}

export async function reorderStreams(ids: string[]): Promise<void> {
  await Promise.all(
    ids.map((id, idx) => supabase.from("streams").update({ sort_order: idx }).eq("id", id)),
  );
}

export async function getStreamByCode(eventId: string, code: string): Promise<Stream | null> {
  const { data } = await supabase
    .from("streams")
    .select("*")
    .eq("event_id", eventId)
    .eq("code", code.toUpperCase())
    .maybeSingle();
  return data as Stream | null;
}

// ─── Insights ──────────────────────────────────────────────────────────────

/**
 * After the tools overhaul there is at most one ai_insights row per
 * (question, tool). Callers that want "the current set" of insights for a
 * question should use this helper.
 */
export async function getLatestInsights(questionId: string) {
  const { data } = await supabase
    .from("ai_insights")
    .select("*")
    .eq("question_id", questionId)
    .order("created_at", { ascending: false });
  return data ?? [];
}

// ─── Manifesto helpers ─────────────────────────────────────────────────────

/** Latest manifesto row for an event (any approval state). */
export async function getLatestManifesto(eventId: string): Promise<AIInsight | null> {
  const { data } = await supabase
    .from("ai_insights")
    .select("*")
    .eq("event_id", eventId)
    .eq("type", "manifesto")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return data as AIInsight | null;
}

/** Latest manifesto row that has been approved (is_approved = true). */
export async function getApprovedLatestManifesto(eventId: string): Promise<AIInsight | null> {
  const { data } = await supabase
    .from("ai_insights")
    .select("*")
    .eq("event_id", eventId)
    .eq("type", "manifesto")
    .eq("is_approved", true)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  return data as AIInsight | null;
}

/** All manifesto rows awaiting approval (is_approved = false), newest first. */
export async function getPendingManifestos(eventId: string): Promise<AIInsight[]> {
  const { data } = await supabase
    .from("ai_insights")
    .select("*")
    .eq("event_id", eventId)
    .eq("type", "manifesto")
    .eq("is_approved", false)
    .order("created_at", { ascending: false });
  return (data ?? []) as AIInsight[];
}

/**
 * Trigger a manifesto generation via the workshop-ai edge function.
 * Pass `force: true` to bypass the server-side throttle.
 */
export async function triggerManifesto(
  eventId: string,
  force: boolean,
): Promise<{ insight_id: string; is_approved: boolean } | { skipped: true; reason: string }> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token;
  if (!accessToken) throw new Error("Not authenticated");

  const res = await fetch(`${SUPABASE_URL}/functions/v1/workshop-ai`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ type: "manifesto", event_id: eventId, force }),
  });

  const raw = await res.text();
  let data: Record<string, unknown> = {};
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    data = { error: raw };
  }

  if (res.status === 200) {
    if (data.skipped === true) {
      return { skipped: true, reason: (data.reason as string) ?? "throttled" };
    }
    return data as { insight_id: string; is_approved: boolean };
  }

  const detail = (data.error as string) || raw || res.statusText;
  throw new Error(`Manifesto request failed (${res.status}): ${detail}`);
}

/** Approve a manifesto row (set is_approved = true). */
export async function approveManifesto(insightId: string): Promise<void> {
  const { error } = await supabase
    .from("ai_insights")
    .update({ is_approved: true })
    .eq("id", insightId);
  if (error) throw error;
}

// ─── Event tools ───────────────────────────────────────────────────────────

export async function getEventTools(eventId: string): Promise<EventTool[]> {
  const { data } = await supabase
    .from("event_tools")
    .select("*")
    .eq("event_id", eventId)
    .order("sort_order", { ascending: true });
  return (data ?? []) as EventTool[];
}

export async function createEventTool(eventId: string, params: {
  tool_key: string;
  label: string;
  icon: string;
  accent: ToolAccent;
  prompt: string;
  scope?: "question" | "event";
  cadence_first: number | null;
  cadence_step: number | null;
  sort_order: number;
  is_enabled?: boolean;
}): Promise<EventTool> {
  const { data, error } = await supabase
    .from("event_tools")
    .insert({
      event_id: eventId,
      tool_key: params.tool_key,
      label: params.label,
      icon: params.icon,
      accent: params.accent,
      prompt: params.prompt,
      scope: params.scope ?? "question",
      cadence_first: params.cadence_first,
      cadence_step: params.cadence_step,
      sort_order: params.sort_order,
      is_enabled: params.is_enabled ?? true,
      is_builtin: false,
    })
    .select()
    .single();
  if (error) throw error;
  return data as EventTool;
}

export async function updateEventTool(id: string, updates: Partial<EventTool>) {
  const { error } = await supabase.from("event_tools").update(updates).eq("id", id);
  if (error) throw error;
}

export async function deleteEventTool(id: string) {
  const { error } = await supabase.from("event_tools").delete().eq("id", id);
  if (error) throw error;
}

export async function reorderEventTools(orderedIds: string[]) {
  await Promise.all(
    orderedIds.map((id, idx) =>
      supabase.from("event_tools").update({ sort_order: idx }).eq("id", id),
    ),
  );
}

/**
 * Restore a built-in tool to its code-side defaults. Lookup is by tool_key.
 * No-op if the tool is custom (no default exists).
 */
export async function resetEventTool(tool: EventTool): Promise<ToolDefault | null> {
  const def = TOOL_DEFAULTS.find((d) => d.tool_key === tool.tool_key);
  if (!def) return null;
  await updateEventTool(tool.id, {
    label: def.label,
    icon: def.icon,
    accent: def.accent,
    prompt: def.prompt,
    scope: "question",
    cadence_first: def.cadence_first,
    cadence_step: def.cadence_step,
    is_enabled: true,
  });
  return def;
}

// ─── Question control ──────────────────────────────────────────────────────

export async function updateQuestion(id: string, updates: Partial<Question>) {
  const { error } = await supabase.from("questions").update(updates).eq("id", id);
  if (error) throw error;
}

export async function setQuestionOpen(questionId: string, open: boolean) {
  const updates: Partial<Question> = { is_open: open };
  if (open) updates.opened_at = new Date().toISOString();
  else updates.closed_at = new Date().toISOString();
  const { error } = await supabase.from("questions").update(updates).eq("id", questionId);
  if (error) throw error;
}

// ─── Event pause (panic kill) ──────────────────────────────────────────────

export async function setEventPaused(eventId: string, paused: boolean) {
  return updateEvent(eventId, { responses_paused: paused });
}

/**
 * Round-trippable rotation pause: moves the current interval into
 * rotation_interval_seconds_saved when pausing, restores it on resume.
 * Co-Moderator allow-list covers both columns.
 */
export async function pauseRotation(eventId: string, paused: boolean): Promise<void> {
  const { data: event } = await supabase
    .from("events")
    .select("rotation_interval_seconds, rotation_interval_seconds_saved")
    .eq("id", eventId)
    .maybeSingle();
  if (!event) throw new Error("Event not found");

  let updates: Partial<Event>;
  if (paused) {
    const current = event.rotation_interval_seconds;
    if (current == null) return; // already paused, no-op
    updates = {
      rotation_interval_seconds: null,
      rotation_interval_seconds_saved: current,
    };
  } else {
    const saved = event.rotation_interval_seconds_saved;
    updates = {
      rotation_interval_seconds: saved ?? null,
      rotation_interval_seconds_saved: null,
    };
  }

  const { error } = await supabase.from("events").update(updates).eq("id", eventId);
  if (error) throw error;
}

// ─── Response moderation ───────────────────────────────────────────────────

export async function hideResponse(id: string, hidden: boolean) {
  const { error } = await supabase.from("responses").update({ is_hidden: hidden }).eq("id", id);
  if (error) throw error;
}

export async function pinResponse(id: string, pinned: boolean) {
  const { error } = await supabase.from("responses").update({ is_pinned: pinned }).eq("id", id);
  if (error) throw error;
}

export async function setResponseModerationOverride(id: string, overridden: boolean) {
  const { error } = await supabase
    .from("responses")
    .update({ moderation_overridden: overridden })
    .eq("id", id);
  if (error) throw error;
}

/**
 * Directly re-classifies a response's moderation status and marks it as
 * facilitator-overridden. Clears borderline_until unless the new status is
 * 'borderline' (where the field is used as a grace-window timestamp).
 */
export async function reclassifyResponse(
  id: string,
  status: "clean" | "borderline" | "block",
): Promise<void> {
  const updates: Partial<Response> = {
    moderation_status: status,
    moderation_overridden: true,
  };
  if (status !== "borderline") {
    updates.borderline_until = null;
  }
  const { error } = await supabase.from("responses").update(updates).eq("id", id);
  if (error) throw error;
}

// Anon-callable. Fire-and-forget from the participant client after a successful insert.
// The edge function is idempotent on non-pending rows, so retries are safe.
export async function triggerModeration(responseId: string): Promise<void> {
  try {
    const deviceSalt = getDeviceSalt();
    await fetch(`${SUPABASE_URL}/functions/v1/moderate-response`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ response_id: responseId, device_salt: deviceSalt }),
    });
  } catch {
    // Swallow — verdict will surface as pending in the facilitator's queue.
  }
}

export async function getModerationReasons(responseIds: string[]): Promise<Record<string, string>> {
  if (responseIds.length === 0) return {};
  const { data } = await supabase
    .from("response_moderation")
    .select("response_id, reason")
    .in("response_id", responseIds);
  const map: Record<string, string> = {};
  (data ?? []).forEach((row) => {
    map[row.response_id] = row.reason;
  });
  return map;
}

// ─── AI insights display ───────────────────────────────────────────────────

export async function setInsightDisplayed(id: string, displayed: boolean) {
  const { error } = await supabase.from("ai_insights").update({ is_displayed: displayed }).eq("id", id);
  if (error) throw error;
}

// ─── AI trigger ────────────────────────────────────────────────────────────

// The edge function builds all payloads server-side (question text, priming,
// visible responses) — clients send only ids. Client-side response arrays are
// still used locally to decide WHEN to trigger (thresholds), never as payload.
export type TriggerAIParams =
  | {
      type: "cluster";
      questionId: string;
      accessToken: string;
      triggerReason?: "initial" | "manual" | "auto_threshold";
    }
  | {
      type: "insight";
      toolId: string;
      force?: boolean;
      questionId: string;
      accessToken: string;
    };

export interface TriggerAIResult {
  /** True if the server's cadence gate refused to run (HTTP 409). */
  skipped?: boolean;
  reason?: string;
  [key: string]: unknown;
}

/**
 * Hit the workshop-ai edge function. Insight runs are subject to the
 * server-side cadence gate; if the gate refuses the function returns 409 and
 * this helper resolves with `{ skipped: true }` instead of throwing — survey
 * fan-outs depend on that. Genuine errors (5xx, auth, bad input) still throw.
 */
export async function triggerAI(params: TriggerAIParams): Promise<TriggerAIResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const freshToken = sessionData.session?.access_token ?? params.accessToken;

  const body: Record<string, unknown> = {
    type: params.type,
    question_id: params.questionId,
  };
  if (params.type === "insight") {
    body.tool_id = params.toolId;
    if (params.force) body.force = true;
  }
  if (params.type === "cluster" && params.triggerReason) {
    body.trigger_reason = params.triggerReason;
  }

  const res = await fetch(`${SUPABASE_URL}/functions/v1/workshop-ai`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${freshToken}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify(body),
  });

  const raw = await res.text();
  let data: Record<string, unknown> = {};
  try {
    data = raw ? JSON.parse(raw) : {};
  } catch {
    data = { error: raw };
  }

  // Cadence / state gates (tool disabled, throttle not due, manual-only, etc.)
  // come back as 200 with `{ skipped: true, error: "..." }`. They are normal
  // server-side decisions, not failures — don't surface as console errors.
  if (data.skipped === true) {
    return { skipped: true, reason: (data.error as string) ?? "cadence" };
  }

  if (!res.ok) {
    const detail = (data.error as string) || raw || res.statusText;
    console.error("[triggerAI] failed", { status: res.status, detail, type: params.type });
    throw new Error(`AI request failed (${res.status}): ${detail}`);
  }
  return data;
}

// ─── Placement & cluster controls ──────────────────────────────────────────

/**
 * Hit the workshop-ai edge function in `place` mode for a single response.
 *
 * The function is idempotent: if `responses.cluster_id` is already set, or the
 * question has no `cluster_runs` row yet, the call returns without an AI hit.
 * Callers (the `useLivePlacement` hook) treat all responses as fire-and-forget:
 * any error is logged but never thrown, because placement is best-effort and a
 * subsequent realtime tick will re-attempt.
 *
 * Uses the participant anon key + a session JWT if present, so it works from
 * both `FacilitatorView` (authenticated) and `StageView` (often anon). Anon
 * RLS on `responses.UPDATE` is irrelevant because the edge function uses the
 * service role key for the write — anon callers only need to *trigger* the
 * function, not modify the row themselves.
 */
export async function requestPlacement(responseId: string): Promise<void> {
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData.session?.access_token ?? SUPABASE_ANON_KEY;

  const res = await fetch(`${SUPABASE_URL}/functions/v1/workshop-ai`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      apikey: SUPABASE_ANON_KEY,
    },
    body: JSON.stringify({ type: "place", response_id: responseId }),
  });

  if (!res.ok) {
    const raw = await res.text();
    throw new Error(`Placement request failed (${res.status}): ${raw}`);
  }
}

/** Toggle the per-question cluster-freeze flag. */
export async function setQuestionFrozen(
  questionId: string,
  isFrozen: boolean,
): Promise<void> {
  const { error } = await supabase
    .from("questions")
    .update({ is_frozen: isFrozen })
    .eq("id", questionId);
  if (error) throw error;
}

/**
 * Response → respondent-key links of an event. RLS limits this to owners and
 * co-admins; the keys never leave `exportEventLog`, which turns them into
 * pseudonymous R-numbers.
 */
export async function getRespondentLinks(eventId: string): Promise<Map<string, string>> {
  const { data, error } = await supabase
    .from("response_respondents")
    .select("response_id, respondent_key")
    .eq("event_id", eventId);
  if (error) throw error;
  return new Map((data ?? []).map((r) => [r.response_id, r.respondent_key]));
}

/** Persist the facilitator's chosen stage view for a question (null = type default). */
export async function setStageView(questionId: string, view: string | null): Promise<void> {
  const { error } = await supabase
    .from("questions")
    .update({ stage_view: view })
    .eq("id", questionId);
  if (error) throw error;
}

/** Distinct respondents + unlinked rows for a question (k-anonymous RPC). */
export async function getRespondentStats(
  questionId: string,
): Promise<{ respondents: number; unlinked_responses: number }> {
  const { data, error } = await supabase.rpc("question_respondent_stats", { p_question: questionId });
  if (error) throw error;
  const row = data?.[0];
  return { respondents: row?.respondents ?? 0, unlinked_responses: row?.unlinked_responses ?? 0 };
}

/** Per-segment answer counts for a question; empty when no segment question
 *  is set, the question is the segment question, or fewer than two segments
 *  clear the minimum size. */
export async function getSegmentDistribution(questionId: string): Promise<SegmentRow[]> {
  const { data, error } = await supabase.rpc("question_segment_distribution", { p_question: questionId });
  if (error) throw error;
  return data ?? [];
}

/**
 * One-shot analysis over a finished (async) dataset: for every question with
 * ≥2 visible responses, run a manual cluster, then every enabled
 * question-scoped insight tool with force, then freeze the question so the
 * live stage at the workshop shows a stable result. Sequential on purpose —
 * the edge function serialises cluster runs via ai_locks anyway, and the
 * per-event AI budget guard stays authoritative.
 */
export async function runEventAnalysis(params: {
  eventId: string;
  questions: Question[];
  tools: EventTool[];
  accessToken: string;
  onProgress?: (note: string) => void;
}): Promise<{ clustered: number; insights: number; meta: number; manifesto: boolean; skipped: number }> {
  const { eventId, questions, tools, accessToken, onProgress } = params;
  const enabledTools = tools.filter((t) => t.is_enabled && t.scope === "question");
  const eventTools = tools.filter((t) => t.is_enabled && t.scope === "event");
  let clustered = 0;
  let insights = 0;
  let meta = 0;
  let manifesto = false;
  let skipped = 0;
  // Event-scoped results live in the slot of the question that triggered
  // them; the first analysed question is that anchor.
  let anchorQuestionId: string | null = null;

  try {
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      const label = `Frage ${i + 1}/${questions.length}`;

      let responses: Response[];
      try {
        responses = await getResponses(q.id);
      } catch (err) {
        console.error("[runEventAnalysis] getResponses failed", q.id, err);
        skipped += 1;
        continue;
      }
      const visible = responses.filter((r) => isVisibleResponse(r));
      if (visible.length < 2) {
        skipped += 1;
        continue;
      }
      anchorQuestionId ??= q.id;

      onProgress?.(`${label}: Clustering …`);
      await setAIStatus(eventId, "clustering", `Analysiere ${label} …`);
      try {
        const res = await triggerAI({
          type: "cluster",
          questionId: q.id,
          accessToken,
          triggerReason: "manual",
        });
        if (!res.skipped) clustered += 1;
      } catch (err) {
        console.error("[runEventAnalysis] cluster failed", q.id, err);
      }

      for (const tool of enabledTools) {
        onProgress?.(`${label}: ${tool.label} …`);
        try {
          const res = await triggerAI({
            type: "insight",
            toolId: tool.id,
            questionId: q.id,
            accessToken,
            force: true,
          });
          if (!res.skipped) insights += 1;
        } catch (err) {
          console.error("[runEventAnalysis] insight failed", tool.tool_key, err);
        }
      }

      try {
        await setQuestionFrozen(q.id, true);
      } catch (err) {
        console.error("[runEventAnalysis] freeze failed", q.id, err);
      }
    }

    // Meta phase: the event-wide tools and the manifesto see every question
    // with its distributions and clusters, so they run after the per-question
    // pass. Both are forced (the cadence guard treats event scope as manual).
    if (anchorQuestionId) {
      for (const tool of eventTools) {
        onProgress?.(`Meta-Analyse: ${tool.label} …`);
        await setAIStatus(eventId, tool.tool_key, `${tool.label} über alle Fragen …`);
        try {
          const res = await triggerAI({
            type: "insight",
            toolId: tool.id,
            questionId: anchorQuestionId,
            accessToken,
            force: true,
          });
          if (!res.skipped) meta += 1;
        } catch (err) {
          console.error("[runEventAnalysis] event tool failed", tool.tool_key, err);
        }
      }
      onProgress?.("Manifest wird geschrieben …");
      await setAIStatus(eventId, "manifesto", "Manifest wird geschrieben …");
      try {
        const res = await triggerManifesto(eventId, true);
        if (!("skipped" in res)) manifesto = true;
      } catch (err) {
        console.error("[runEventAnalysis] manifesto failed", err);
      }
    }
  } finally {
    await setAIStatus(eventId, null, null);
  }

  return { clustered, insights, meta, manifesto, skipped };
}

/** Replace the seed_clusters jsonb column. Pass `null` to clear. */
export async function updateQuestionSeeds(
  questionId: string,
  seeds: SeedCluster[] | null,
): Promise<void> {
  const { error } = await supabase
    .from("questions")
    .update({ seed_clusters: seeds })
    .eq("id", questionId);
  if (error) throw error;
}

/** Update the per-event recluster_threshold (not exposed in MVP UI, used by DB tweaks). */
export async function setEventReclusterThreshold(
  eventId: string,
  threshold: number,
): Promise<void> {
  return updateEvent(eventId, { recluster_threshold: threshold });
}

/** Manually assign a response to a cluster. Clears placement_confidence. */
export async function assignResponseToCluster(
  responseId: string,
  clusterId: string,
): Promise<void> {
  const { error } = await supabase
    .from("responses")
    .update({ cluster_id: clusterId, placement_confidence: null })
    .eq("id", responseId);
  if (error) throw error;
}

// ─── Stadtteil / Geo aggregation ──────────────────────────────────────────

export type StadtteilAggregate = {
  geo_tag: string;
  stream_count: number;
  response_count: number;
  /** v1 simplification: top 3 clusters by size across the event's latest cluster
   * runs. These are global cluster sizes, not filtered to responses from this
   * geo_tag specifically (we don't store per-response cluster assignments).
   * v2: store cluster_id on responses so we can do per-geo_tag cluster breakdowns. */
  top_clusters: { label: string; size: number }[];
  /** Up to 5 example response snippets (first 80 chars), anonymised. */
  example_responses: { text: string; cluster_label: string | null }[];
};

/**
 * Aggregate response data grouped by geo_tag for the Stadtteil heatmap.
 *
 * 1. Loads all streams for the event with non-null geo_tag.
 * 2. Loads all public responses (moderation_status 'clean', or 'borderline'
 *    past its grace window) whose stream_id matches those streams.
 * 3. Loads the latest cluster run per question for the event; picks the top 3
 *    clusters globally as a v1 proxy for "dominant themes".
 * 4. Groups results by geo_tag (multiple streams can share a geo_tag).
 */
/** True when the event has at least one geo-tagged stream. Drives whether the
 *  public insights page renders the Stadtteil heatmap section at all. */
export async function hasGeoStreams(eventId: string): Promise<boolean> {
  const { data } = await supabase
    .from("streams")
    .select("id")
    .eq("event_id", eventId)
    .not("geo_tag", "is", null)
    .limit(1);
  return (data ?? []).length > 0;
}

export async function getResponsesByStream(eventId: string): Promise<StadtteilAggregate[]> {
  // Step 1: streams with geo_tag
  const { data: streamRows } = await supabase
    .from("streams")
    .select("id, geo_tag")
    .eq("event_id", eventId)
    .not("geo_tag", "is", null);

  const streams = (streamRows ?? []) as { id: string; geo_tag: string }[];
  if (streams.length === 0) return [];

  const streamIds = streams.map((s) => s.id);
  const now = new Date().toISOString();

  // Step 2: public responses for those streams (clean + borderline-past-grace)
  const [cleanResult, borderlineResult] = await Promise.all([
    supabase
      .from("responses")
      .select("id, text, stream_id")
      .in("stream_id", streamIds)
      .eq("moderation_status", "clean")
      .eq("is_hidden", false),
    supabase
      .from("responses")
      .select("id, text, stream_id")
      .in("stream_id", streamIds)
      .eq("moderation_status", "borderline")
      .eq("is_hidden", false)
      .lte("borderline_until", now),
  ]);

  const publicResponses = [
    ...(cleanResult.data ?? []),
    ...(borderlineResult.data ?? []),
  ] as { id: string; text: string; stream_id: string }[];

  // Step 3: latest clusters across the event (global, v1 simplification)
  const { data: questionRows } = await supabase
    .from("questions")
    .select("id")
    .eq("event_id", eventId);

  const questionIds = (questionRows ?? []).map((q: { id: string }) => q.id);

  let globalTopClusters: { label: string; size: number }[] = [];
  if (questionIds.length > 0) {
    // Get latest run per question, then collect cluster sizes
    const runResults = await Promise.all(
      questionIds.map((qId: string) =>
        supabase
          .from("cluster_runs")
          .select("id")
          .eq("question_id", qId)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ),
    );
    const runIds = runResults
      .map((r) => r.data?.id as string | undefined)
      .filter((id): id is string => id !== undefined);

    if (runIds.length > 0) {
      const { data: clusterRows } = await supabase
        .from("clusters")
        .select("label, size")
        .in("run_id", runIds)
        .order("size", { ascending: false });

      // Merge clusters with the same label, take top 3
      const merged = new Map<string, number>();
      for (const c of clusterRows ?? []) {
        const row = c as { label: string; size: number };
        merged.set(row.label, (merged.get(row.label) ?? 0) + row.size);
      }
      globalTopClusters = Array.from(merged.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([label, size]) => ({ label, size }));
    }
  }

  // Step 4: normalize each stream's geo_tag to a canonical Stadtbezirk slug
  // before grouping. Without this, user-entered values like "Mitte" or
  // "Linden-Limmer" never match the heatmap's slug lookup and the districts
  // stay unfilled even when they have responses.
  const streamGeoMap = new Map<string, string>(
    streams.map((s) => [s.id, slugifyGeoTag(s.geo_tag) ?? s.geo_tag]),
  );

  // Count streams per geo_tag (using the normalized slug where applicable)
  const streamCountByGeo = new Map<string, number>();
  for (const s of streams) {
    const key = slugifyGeoTag(s.geo_tag) ?? s.geo_tag;
    streamCountByGeo.set(key, (streamCountByGeo.get(key) ?? 0) + 1);
  }

  // Group responses
  const responsesByGeo = new Map<string, { id: string; text: string; stream_id: string }[]>();
  for (const r of publicResponses) {
    const geo = streamGeoMap.get(r.stream_id);
    if (!geo) continue;
    if (!responsesByGeo.has(geo)) responsesByGeo.set(geo, []);
    responsesByGeo.get(geo)!.push(r);
  }

  // Build aggregates
  const allGeoTags = new Set([
    ...streamCountByGeo.keys(),
    ...responsesByGeo.keys(),
  ]);

  const result: StadtteilAggregate[] = [];
  for (const geo of allGeoTags) {
    const responses = responsesByGeo.get(geo) ?? [];
    result.push({
      geo_tag: geo,
      stream_count: streamCountByGeo.get(geo) ?? 0,
      response_count: responses.length,
      top_clusters: globalTopClusters,
      example_responses: responses.slice(0, 5).map((r) => ({
        text: r.text.slice(0, 80),
        cluster_label: null, // v1: no per-response cluster assignment
      })),
    });
  }

  return result;
}

// ─── Public response count ─────────────────────────────────────────────────

/**
 * Returns the count of public (visible) responses for all questions in an
 * event. Counts rows where moderation_status IN ('clean', 'borderline') and,
 * for borderline rows, borderline_until <= now(). This mirrors the
 * isPublicResponse logic without pulling every response row to the client.
 */
export async function getEventPublicResponseCount(eventId: string): Promise<number> {
  const { data: questionRows } = await supabase
    .from("questions")
    .select("id")
    .eq("event_id", eventId);
  const questionIds = (questionRows ?? []).map((q) => q.id);
  if (questionIds.length === 0) return 0;

  const now = new Date().toISOString();

  // Count clean responses
  const { count: cleanCount } = await supabase
    .from("responses")
    .select("id", { count: "exact", head: true })
    .in("question_id", questionIds)
    .eq("moderation_status", "clean")
    .eq("is_hidden", false);

  // Count borderline responses that are past their grace window
  const { count: borderlineCount } = await supabase
    .from("responses")
    .select("id", { count: "exact", head: true })
    .in("question_id", questionIds)
    .eq("moderation_status", "borderline")
    .eq("is_hidden", false)
    .lte("borderline_until", now);

  return (cleanCount ?? 0) + (borderlineCount ?? 0);
}

// ─── AI status ─────────────────────────────────────────────────────────────

export async function setAIStatus(eventId: string, status: string | null, label: string | null) {
  await supabase
    .from("events")
    .update({ ai_status: status, ai_status_label: label })
    .eq("id", eventId);
}

export async function setSessionAIStatus(sessionId: string, status: string | null, label: string | null) {
  await supabase
    .from("sessions")
    .update({ ai_status: status, ai_status_label: label })
    .eq("id", sessionId);
}

// ─── Reset event ───────────────────────────────────────────────────────────

/**
 * Delete every AI result of an event — cluster runs with their clusters (theme
 * history cascades), per-question insights and the event-level manifesto — and
 * clear "Cluster fixiert" on all questions. Answers, open/closed state and the
 * chosen stage views stay; answers lose their cluster assignment automatically
 * (responses.cluster_id is ON DELETE SET NULL). Used by the console's
 * "Nur KI-Ergebnisse löschen" reset and as the first half of resetEvent.
 */
export async function resetEventAI(eventId: string): Promise<void> {
  const questions = await getQuestionsByEvent(eventId);
  const questionIds = questions.map((q) => q.id);

  if (questionIds.length > 0) {
    const insights = await supabase.from("ai_insights").delete().in("question_id", questionIds);
    if (insights.error) throw insights.error;
    const { data: runs, error: runsErr } = await supabase
      .from("cluster_runs")
      .select("id")
      .in("question_id", questionIds);
    if (runsErr) throw runsErr;
    if (runs && runs.length > 0) {
      const clusters = await supabase.from("clusters").delete().in("run_id", runs.map((r) => r.id));
      if (clusters.error) throw clusters.error;
    }
    const runDel = await supabase.from("cluster_runs").delete().in("question_id", questionIds);
    if (runDel.error) throw runDel.error;
    const unfreeze = await supabase.from("questions").update({ is_frozen: false }).in("id", questionIds);
    if (unfreeze.error) throw unfreeze.error;
  }

  const eventInsights = await supabase.from("ai_insights").delete().eq("event_id", eventId);
  if (eventInsights.error) throw eventInsights.error;
}

export async function resetEvent(eventId: string) {
  await resetEventAI(eventId);
  const questions = await getQuestionsByEvent(eventId);
  const questionIds = questions.map((q) => q.id);

  if (questionIds.length > 0) {
    await supabase.from("responses").delete().in("question_id", questionIds);
    for (const q of questions) {
      await supabase
        .from("questions")
        .update({ is_open: false, opened_at: null, closed_at: null })
        .eq("id", q.id);
    }
  }

  await supabase.from("events").update({ status: "waiting", active_question_index: 0 }).eq("id", eventId);
}

export async function resetSession(sessionId: string) {
  const questions = await getQuestions(sessionId);
  const questionIds = questions.map((q) => q.id);

  if (questionIds.length > 0) {
    await supabase.from("ai_insights").delete().in("question_id", questionIds);
    const { data: runs } = await supabase.from("cluster_runs").select("id").in("question_id", questionIds);
    if (runs && runs.length > 0) {
      await supabase.from("clusters").delete().in("run_id", runs.map((r) => r.id));
    }
    await supabase.from("cluster_runs").delete().in("question_id", questionIds);
    await supabase.from("responses").delete().in("question_id", questionIds);
    for (const q of questions) {
      await supabase
        .from("questions")
        .update({ is_open: false, opened_at: null, closed_at: null })
        .eq("id", q.id);
    }
  }

  await supabase.from("sessions").update({ status: "waiting", active_question_index: 0 }).eq("id", sessionId);
}

// ─── Event statistics ──────────────────────────────────────────────────────

/**
 * Returns per-event usage statistics: response counts by moderation status,
 * a best-effort "active in last 5 min" concurrency count, and AI call/cost
 * aggregates from ai_call_log. Queries run in parallel where possible.
 *
 * Note: "current_concurrency" is responses submitted in the last 5 minutes,
 * not literal connected WebSocket clients.
 */
export async function getEventStats(eventId: string): Promise<EventStats> {
  // First, resolve question IDs for this event — needed for responses queries.
  const { data: questionRows } = await supabase
    .from("questions")
    .select("id")
    .eq("event_id", eventId);
  const questionIds = (questionRows ?? []).map((q) => q.id);

  const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();

  const [
    totalResult,
    statusResult,
    concurrencyResult,
    aiCallsResult,
    aiSpendResult,
    aiModelResult,
  ] = await Promise.all([
    // Total responses across all questions for this event
    questionIds.length > 0
      ? supabase
          .from("responses")
          .select("id", { count: "exact", head: true })
          .in("question_id", questionIds)
      : Promise.resolve({ count: 0, data: null, error: null }),

    // Responses (with status) across all questions
    questionIds.length > 0
      ? supabase
          .from("responses")
          .select("moderation_status")
          .in("question_id", questionIds)
      : Promise.resolve({ data: [], error: null }),

    // Responses submitted in the last 5 minutes (best-effort concurrency proxy)
    questionIds.length > 0
      ? supabase
          .from("responses")
          .select("id", { count: "exact", head: true })
          .in("question_id", questionIds)
          .gt("submitted_at", fiveMinAgo)
      : Promise.resolve({ count: 0, data: null, error: null }),

    // AI calls grouped by function_name
    supabase
      .from("ai_call_log")
      .select("function_name")
      .eq("event_id", eventId),

    // Total AI spend
    supabase
      .from("ai_call_log")
      .select("cost_usd")
      .eq("event_id", eventId),

    // AI calls grouped by model
    supabase
      .from("ai_call_log")
      .select("model")
      .eq("event_id", eventId),
  ]);

  // Tally response statuses
  const statusCounts = { clean: 0, borderline: 0, block: 0, pending: 0 };
  for (const row of statusResult.data ?? []) {
    const s = (row as { moderation_status: string }).moderation_status as keyof typeof statusCounts;
    if (s in statusCounts) statusCounts[s]++;
  }

  // Tally AI calls by function
  const aiCallCountByFunction: Record<string, number> = {};
  for (const row of aiCallsResult.data ?? []) {
    const fn = (row as { function_name: string }).function_name;
    aiCallCountByFunction[fn] = (aiCallCountByFunction[fn] ?? 0) + 1;
  }

  // Total AI spend
  let aiSpendTotal = 0;
  for (const row of aiSpendResult.data ?? []) {
    aiSpendTotal += Number((row as { cost_usd: number }).cost_usd) || 0;
  }

  // Tally AI calls by model
  const aiCallsByModel: Record<string, number> = {};
  for (const row of aiModelResult.data ?? []) {
    const m = (row as { model: string }).model;
    aiCallsByModel[m] = (aiCallsByModel[m] ?? 0) + 1;
  }

  return {
    total_responses: (totalResult as { count: number | null }).count ?? 0,
    responses_by_status: statusCounts,
    current_concurrency: (concurrencyResult as { count: number | null }).count ?? 0,
    ai_call_count_by_function: aiCallCountByFunction,
    ai_calls_by_model: aiCallsByModel,
    ai_spend_usd_total: aiSpendTotal,
  };
}

// ─── Export ────────────────────────────────────────────────────────────────

export async function exportEventLog(eventId: string) {
  const event = await getEventById(eventId);
  if (!event) throw new Error("Event not found");

  const questions = await getQuestionsByEvent(eventId);
  const tools = await getEventTools(eventId);
  const toolKeyById = new Map(tools.map((t) => [t.id, t.tool_key]));

  // Pseudonymous respondent labels (R1, R2, …) so answers of one person can be
  // related across questions in the export. Whether that fits what the
  // participants were told is the facilitator's call, not a technical gate.
  const links = await getRespondentLinks(eventId);
  const responsesByQuestion = new Map<string, Response[]>();
  for (const q of questions) responsesByQuestion.set(q.id, await getResponses(q.id));
  const numbers = respondentNumbers(
    [...responsesByQuestion.values()]
      .flat()
      .filter((r) => links.has(r.id))
      .map((r) => ({ respondent_key: links.get(r.id) as string, submitted_at: r.submitted_at })),
  );
  const respondentOf = (responseId: string): string | null => {
    const key = links.get(responseId);
    return key ? numbers.get(key) ?? null : null;
  };

  const log: Record<string, unknown> = {
    exported_at: new Date().toISOString(),
    event: {
      id: event.id,
      title: event.title,
      join_code: event.join_code,
      status: event.status,
      created_at: event.created_at,
    },
    respondents: numbers.size,
    respondent_note:
      "respondent = pseudonyme Nummer je Gerät (R1, R2, …), stabil über Exporte hinweg; null = Antwort ohne Zuordnung (z. B. von der Moderation eingetragen).",
    questions: [],
  };

  for (const q of questions) {
    const responses = responsesByQuestion.get(q.id) ?? [];
    const clusters = await getLatestClusters(q.id);
    const insights = await getLatestInsights(q.id);
    const reasons = await getModerationReasons(responses.map((r) => r.id));
    const visible = responses.filter((r) => isVisibleResponse(r));
    // Respondent base for multi-select percentages; the export must not fail
    // when the RPC is unavailable, so fall back to the selection count.
    let base: RespondentBase = null;
    if (q.question_type === "multiple_choice") {
      try {
        const s = await getRespondentStats(q.id);
        base = { respondents: s.respondents, unlinked: s.unlinked_responses };
      } catch (err) {
        console.warn("[exportEventLog] respondent stats unavailable", q.id, err);
      }
    }

    (log.questions as unknown[]).push({
      order_index: q.order_index,
      text: q.text,
      hint: q.hint,
      question_type: q.question_type,
      config: q.config,
      opened_at: q.opened_at,
      closed_at: q.closed_at,
      distribution: exportDistribution(q, visible, base),
      responses: responses.map((r) => ({
        respondent: respondentOf(r.id),
        text: r.text,
        rating: r.rating,
        choice_key: r.choice_key,
        choice_label: answerLabel(q, r),
        is_other: r.is_other,
        submitted_at: r.submitted_at,
        is_hidden: r.is_hidden,
        moderation_status: r.moderation_status,
        moderation_category: r.moderation_category,
        moderation_overridden: r.moderation_overridden,
        moderated_at: r.moderated_at,
        moderation_reason: reasons[r.id] ?? null,
      })),
      clusters: clusters.map((c) => ({
        label: c.label,
        size: c.size,
        representative_entries: c.representative_entries,
        rank_order: c.rank_order,
      })),
      ai_insights: insights.map((i) => ({
        tool_key: i.tool_id ? toolKeyById.get(i.tool_id) ?? null : i.type,
        content: i.content,
        responses_at_run: i.responses_at_run,
        created_at: i.created_at,
      })),
    });
  }

  return log;
}

export async function exportSessionLog(sessionId: string) {
  const session = await getSession();
  if (!session) throw new Error("No session found");

  const questions = await getQuestions(sessionId);
  const log: Record<string, unknown> = {
    exported_at: new Date().toISOString(),
    session: {
      id: session.id,
      title: session.title,
      status: session.status,
      created_at: session.created_at,
    },
    questions: [],
  };

  for (const q of questions) {
    const responses = await getResponses(q.id);
    const clusters = await getLatestClusters(q.id);
    const insights = await getLatestInsights(q.id);

    (log.questions as unknown[]).push({
      order_index: q.order_index,
      text: q.text,
      hint: q.hint,
      opened_at: q.opened_at,
      closed_at: q.closed_at,
      responses: responses.map((r) => ({
        text: r.text,
        submitted_at: r.submitted_at,
        is_hidden: r.is_hidden,
      })),
      clusters: clusters.map((c) => ({
        label: c.label,
        size: c.size,
        representative_entries: c.representative_entries,
        rank_order: c.rank_order,
      })),
      ai_insights: insights.map((i) => ({
        type: i.type,
        content: i.content,
        created_at: i.created_at,
      })),
    });
  }

  return log;
}
